import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { DemTypeahead, type DemTypeaheadOption } from './DemTypeahead';
import { OrderBatchesItemsPanel } from './OrderBatchesItemsPanel';
import { OrderCheckedControl } from './OrderCheckedControl';
import { useOrderBlockWarnings } from '../../lib/demOrderBlockWarnings';
import { formatProcessingDuration } from '../../lib/demProcessingTime';
import { loadPoMappingContext, type DemPoItemOption } from '../../lib/demPoMapping';
import { fetchItemDetails } from '../../lib/demSkuMap';
import {
  buildOrderActivityTimeline,
  canEditPendingOrder,
  parseDeliveryDateInput,
  sourceDetailTitle,
  updatePendingOrder,
  type DemOrderActivityEntry,
} from '../../lib/demOrderEdit';
import type { DemCustomer, DemUnifiedOrder } from '../../types/dem';

type LineDraft = {
  id: string;
  itemCode: string;
  description: string;
  qty: string;
  uom: string;
};

type Props = {
  order: DemUnifiedOrder | null;
  open: boolean;
  onClose: () => void;
  actor: string;
  actorIds: string[];
  onSaved?: () => void;
  onAmend?: () => void;
  amendDisabled?: boolean;
  checkBusy?: boolean;
  onMarkChecked?: () => void;
  onUnmarkChecked?: () => void;
  onWaiveFee?: () => void;
  waiveBusy?: boolean;
  /** Optional schedule line for Manual orders. */
  scheduleLabel?: string | null;
};

function newLine(): LineDraft {
  return {
    id: crypto.randomUUID(),
    itemCode: '',
    description: '',
    qty: '',
    uom: 'PCS',
  };
}

function formatActivityTime(raw: string): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

export function OrderDetailShell({
  order,
  open,
  onClose,
  actor,
  actorIds,
  onSaved,
  onAmend,
  amendDisabled,
  checkBusy,
  onMarkChecked,
  onUnmarkChecked,
  onWaiveFee,
  waiveBusy,
  scheduleLabel,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activity, setActivity] = useState<DemOrderActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [customerCode, setCustomerCode] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [refNo, setRefNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [email, setEmail] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);

  const [customers, setCustomers] = useState<DemCustomer[]>([]);
  const [itemOptions, setItemOptions] = useState<DemPoItemOption[]>([]);
  const [contextLoading, setContextLoading] = useState(false);

  const canEdit = Boolean(
    order &&
      canEditPendingOrder({
        status: order.status,
        batches: order.batches,
        actorIds,
      }),
  );

  const itemCodes = useMemo(
    () => (order?.items ?? []).map((item) => item.item_code),
    [order?.items],
  );
  const { customerWarning, itemWarnings } = useOrderBlockWarnings(
    editing ? customerCode : order?.customer_code,
    editing ? lines.map((l) => l.itemCode) : itemCodes,
    open && Boolean(order),
  );

  const blankish = (value: string) => {
    const v = value.trim();
    return !v || v === '—' ? '' : v;
  };

  const resetDraftFromOrder = useCallback((row: DemUnifiedOrder) => {
    setCustomerCode(row.customer_code);
    setDeliveryDate(parseDeliveryDateInput(row.delivery_date) || parseDeliveryDateInput(row.delivery_date_display));
    setRefNo(blankish(row.ref_no));
    setRemarks(blankish(row.remarks));
    setContactPerson(blankish(row.contact_person));
    setContactNo(blankish(row.contact_no));
    setEmail(blankish(row.email));
    const batch0 = [...(row.batches ?? [])].sort((a, b) => a.batch_no - b.batch_no)[0];
    const batch0Items = (row.items ?? []).filter((item) => {
      if (!batch0?.id) return true;
      const bid = item.batch_id?.trim() ?? '';
      return !bid || bid === batch0.id;
    });
    setLines(
      batch0Items.length
        ? batch0Items.map((item) => ({
            id: crypto.randomUUID(),
            itemCode: item.item_code,
            description: item.description,
            qty: String(item.qty),
            uom: item.uom || 'PCS',
          }))
        : [newLine()],
    );
  }, []);

  useEffect(() => {
    if (!open || !order) {
      setEditing(false);
      setError(null);
      setActivity([]);
      return;
    }
    setEditing(false);
    setError(null);
    resetDraftFromOrder(order);

    let cancelled = false;
    setActivityLoading(true);
    void buildOrderActivityTimeline({
      order_id: order.order_id,
      created_by: order.created_by,
      created_at: order.created_at,
      checked_at: order.checked_at,
      checked_by: order.checked_by,
      batches: order.batches,
    })
      .then((entries) => {
        if (!cancelled) setActivity(entries);
      })
      .catch(() => {
        if (!cancelled) setActivity([]);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, order, resetDraftFromOrder]);

  useEffect(() => {
    if (!open || !editing) return;
    let cancelled = false;
    setContextLoading(true);
    void loadPoMappingContext()
      .then((ctx) => {
        if (cancelled) return;
        setCustomers(ctx.customers.filter((c) => c.status !== 'Inactive'));
        setItemOptions(ctx.itemOptions);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load customers/items.');
        }
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing]);

  const customerOptions: DemTypeaheadOption[] = useMemo(
    () =>
      customers.map((c) => ({
        value: c.code,
        label: `${c.code} - ${c.name}`,
        blocked: c.is_blocked,
        customerName: c.name,
      })),
    [customers],
  );

  const itemTypeaheadOptions: DemTypeaheadOption[] = useMemo(
    () =>
      itemOptions.map((item) => ({
        value: item.code,
        label: item.code,
        sublabel: item.description,
      })),
    [itemOptions],
  );

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const commitItemCode = async (lineId: string, code: string) => {
    const next = code.trim().toUpperCase();
    updateLine(lineId, { itemCode: next });
    if (!next) {
      updateLine(lineId, { description: '', uom: 'PCS' });
      return;
    }
    try {
      const details = await fetchItemDetails([next]);
      const detail = details[next];
      const opt = itemOptions.find((i) => i.code.toUpperCase() === next);
      updateLine(lineId, {
        description: detail?.description || opt?.description || '',
        uom: detail?.uom || opt?.uom || 'PCS',
      });
    } catch {
      const opt = itemOptions.find((i) => i.code.toUpperCase() === next);
      if (opt) updateLine(lineId, { description: opt.description, uom: opt.uom || 'PCS' });
    }
  };

  const startEdit = () => {
    if (!order || !canEdit) return;
    resetDraftFromOrder(order);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (order) resetDraftFromOrder(order);
    setError(null);
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!order) return;
    setBusy(true);
    setError(null);
    try {
      await updatePendingOrder({
        order_id: order.order_id,
        changed_by: actor,
        actorIds,
        header: {
          customer_code: customerCode,
          delivery_date: deliveryDate,
          ref_no: refNo,
          remarks,
          contact_person: contactPerson,
          contact_no: contactNo,
          email,
        },
        items: lines.map((line) => ({
          item_code: line.itemCode,
          description: line.description,
          qty: Number(line.qty),
          uom: line.uom,
        })),
      });
      setEditing(false);
      try {
        const entries = await buildOrderActivityTimeline({
          order_id: order.order_id,
          created_by: order.created_by,
          created_at: order.created_at,
          checked_at: order.checked_at,
          checked_by: order.checked_by,
          batches: order.batches,
        });
        setActivity(entries);
      } catch {
        /* parent reload will refresh activity */
      }
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setBusy(false);
    }
  };

  if (!order) return null;

  const canWaive =
    order.source === 'Web' &&
    order.status === 'Pending' &&
    order.has_delivery_fee &&
    !order.waive_delivery &&
    Boolean(onWaiveFee);

  const showContacts =
    order.source === 'Web' || order.source === 'WhatsApp' || Boolean(order.contact_person || order.contact_no || order.email);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      title={sourceDetailTitle(order.source, order.order_id)}
      orderDetail
      panelClassName="dem-order-detail-shell"
    >
      <div className="dem-order-detail">
        <div className="dem-order-detail__main">
          <div className="dem-order-detail__toolbar">
            <div className="dem-order-detail__toolbar-meta">
              <span className={`dem-order-detail__status dem-order-detail__status--${order.status.toLowerCase()}`}>
                {order.status}
              </span>
              <span className="dem-order-detail__source">{order.source}</span>
            </div>
            <div className="dem-order-detail__toolbar-actions">
              {canEdit && !editing ? (
                <button type="button" className="btn-secondary btn-sm" onClick={startEdit}>
                  Edit order
                </button>
              ) : null}
              {editing ? (
                <>
                  <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={cancelEdit}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary btn-sm" disabled={busy || contextLoading} onClick={() => void saveEdit()}>
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {error ? <p className="alert alert-error">{error}</p> : null}

          <div className="dem-order-detail__card">
            <div className="dem-order-detail__grid">
              <div>
                <span className="dem-order-detail__label">Order ID</span>
                <strong>{order.order_id}</strong>
              </div>
              <div>
                <span className="dem-order-detail__label">
                  {order.source === 'CallIn' ? 'PO / Ref' : 'PO number'}
                </span>
                {editing ? (
                  <input className="field-input" value={refNo} onChange={(e) => setRefNo(e.target.value)} />
                ) : (
                  <strong>{order.ref_no || '—'}</strong>
                )}
              </div>
              <div>
                <span className="dem-order-detail__label">Customer</span>
                {editing ? (
                  <DemTypeahead
                    value={customerCode}
                    onChange={(next) => setCustomerCode(next)}
                    onCommit={(next) => {
                      setCustomerCode(next.trim());
                    }}
                    commitOnType={false}
                    options={customerOptions}
                    placeholder={contextLoading ? 'Loading…' : 'Customer no.'}
                    disabled={contextLoading || busy}
                    tableMode="customer"
                    inputClassName="field-input"
                  />
                ) : (
                  <strong>
                    {order.customer_code ? `[${order.customer_code}] ` : ''}
                    {order.customer_name}
                  </strong>
                )}
                {customerWarning ? (
                  <p className="dem-order-detail__warn" title={customerWarning}>
                    {customerWarning}
                  </p>
                ) : null}
              </div>
              <div>
                <span className="dem-order-detail__label">
                  {order.source === 'CallIn' ? 'Shipment date' : 'Delivery date'}
                </span>
                {editing ? (
                  <input
                    type="date"
                    className="field-input"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                  />
                ) : (
                  <span>{order.delivery_date_display}</span>
                )}
              </div>
              <div>
                <span className="dem-order-detail__label">Created at</span>
                <span>{order.created_at_display}</span>
              </div>
              <div>
                <span className="dem-order-detail__label">Created by</span>
                <span>{order.created_by}</span>
              </div>
              {showContacts ? (
                <>
                  <div>
                    <span className="dem-order-detail__label">Contact person</span>
                    {editing ? (
                      <input
                        className="field-input"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                      />
                    ) : (
                      <span>{order.contact_person || '—'}</span>
                    )}
                  </div>
                  <div>
                    <span className="dem-order-detail__label">Contact no.</span>
                    {editing ? (
                      <input className="field-input" value={contactNo} onChange={(e) => setContactNo(e.target.value)} />
                    ) : (
                      <span>{order.contact_no || '—'}</span>
                    )}
                  </div>
                  <div>
                    <span className="dem-order-detail__label">Email</span>
                    {editing ? (
                      <input className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} />
                    ) : (
                      <span>{order.email || '—'}</span>
                    )}
                  </div>
                </>
              ) : null}
              <div className="dem-order-detail__span-2">
                <span className="dem-order-detail__label">
                  {order.source === 'CallIn' ? 'Delivery instruction' : 'Remarks'}
                </span>
                {editing ? (
                  <textarea
                    className="field-input"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                ) : (
                  <span>{order.remarks.trim() || '—'}</span>
                )}
              </div>
              {scheduleLabel != null ? (
                <div>
                  <span className="dem-order-detail__label">Delivery schedule</span>
                  <span>{scheduleLabel || '—'}</span>
                </div>
              ) : null}
              <div>
                <span className="dem-order-detail__label">Processing time</span>
                <span>{formatProcessingDuration(order.processing_seconds)}</span>
              </div>
              <div>
                <span className="dem-order-detail__label">Exported by</span>
                <span>{order.exported_by || '—'}</span>
              </div>
              <div>
                <span className="dem-order-detail__label">NAV checked</span>
                {!editing && onMarkChecked && onUnmarkChecked ? (
                  <OrderCheckedControl
                    order={order}
                    busy={checkBusy}
                    onMark={onMarkChecked}
                    onUnmark={onUnmarkChecked}
                  />
                ) : (
                  <span>{order.checked_at ? `Checked · ${order.checked_by}` : '—'}</span>
                )}
              </div>
            </div>
          </div>

          {editing ? (
            <div className="dem-order-detail__card dem-order-detail__edit-items">
              <div className="dem-order-detail__edit-items-head">
                <h3>Items (original batch)</h3>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => setLines((prev) => [...prev, newLine()])}
                >
                  + Line
                </button>
              </div>
              <div className="dem-order-detail__edit-lines">
                <div className="dem-order-detail__edit-line dem-order-detail__edit-line--head" aria-hidden>
                  <span>Item</span>
                  <span>Description</span>
                  <span>Qty</span>
                  <span>UOM</span>
                  <span />
                </div>
                {lines.map((line) => (
                  <div key={line.id} className="dem-order-detail__edit-line">
                    <DemTypeahead
                      value={line.itemCode}
                      onChange={(next) => updateLine(line.id, { itemCode: next.toUpperCase() })}
                      onCommit={(next) => void commitItemCode(line.id, next)}
                      commitOnType={false}
                      options={itemTypeaheadOptions}
                      placeholder="SKU"
                      disabled={busy || contextLoading}
                      tableMode
                      inputClassName="field-input"
                    />
                    <input
                      className="field-input"
                      value={line.description}
                      placeholder="Description"
                      readOnly
                      disabled
                      title="Filled from item code"
                    />
                    <input
                      className="field-input"
                      value={line.qty}
                      inputMode="decimal"
                      onChange={(e) => updateLine(line.id, { qty: e.target.value })}
                    />
                    <input
                      className="field-input"
                      value={line.uom}
                      readOnly
                      disabled
                      title="Filled from item code"
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy || lines.length <= 1}
                      aria-label="Remove line"
                      onClick={() => setLines((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== line.id)))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dem-order-detail__card">
              <OrderBatchesItemsPanel
                orderStatus={order.status}
                batches={order.batches}
                items={order.items}
                onAmend={onAmend}
                amendDisabled={amendDisabled || editing}
                styleDeliveryFee={order.source === 'Web' || order.source === 'WhatsApp'}
                waiveDelivery={order.waive_delivery}
                itemWarnings={itemWarnings}
              />
              {(order.source === 'Web' || order.source === 'WhatsApp') && (
                <div className="dem-order-detail__footer">
                  <strong className="dem-order-detail__total">Total: ${order.total_amount.toFixed(2)}</strong>
                  {canWaive ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={waiveBusy}
                      onClick={onWaiveFee}
                    >
                      {waiveBusy ? 'Waiving…' : 'Waive delivery fee'}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="dem-order-detail__rail" aria-label="Order activity">
          <h3 className="dem-order-detail__rail-title">Activity</h3>
          {activityLoading ? (
            <p className="dem-order-detail__rail-empty">Loading…</p>
          ) : activity.length === 0 ? (
            <p className="dem-order-detail__rail-empty">No activity yet.</p>
          ) : (
            <ul className="dem-order-detail__activity">
              {activity.map((entry) => (
                <li key={entry.id} className={`dem-order-detail__activity-item dem-order-detail__activity-item--${entry.kind}`}>
                  <div className="dem-order-detail__activity-summary">{entry.summary}</div>
                  <div className="dem-order-detail__activity-meta">
                    <span>{entry.by || '—'}</span>
                    <span>{formatActivityTime(entry.at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </Modal>
  );
}
